import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Trash2, Ticket, Plus, Tag } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", discount_type: "percent", percent_off: "", amount_off: "", duration: "once", duration_in_months: "", code: "", max_redemptions: "" };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [mode, setMode] = useState("test");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data.coupons); setMode(data.mode);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load coupons."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/coupons/sync"); setCoupons(data.coupons); toast.success(`Synced ${data.synced} coupon(s) from Stripe.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name, discount_type: form.discount_type, duration: form.duration,
        code: form.code || undefined,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
      };
      if (form.discount_type === "percent") payload.percent_off = Number(form.percent_off);
      else payload.amount_off = Number(form.amount_off);
      if (form.duration === "repeating") payload.duration_in_months = Number(form.duration_in_months);
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created and synced to Stripe.");
      setForm(EMPTY); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/coupons/${deleting.id}`); toast.success("Coupon deleted from Stripe."); setDeleting(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const discountLabel = (c) => c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `$${(c.amount_off / 100).toFixed(2)} ${(c.currency || "").toUpperCase()} off` : "—";

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="Billing" title="Coupons" subtitle="Create discount codes for Family Path. Changes sync to Stripe both ways." />
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${mode === "live" ? "bg-kid-green/15 text-[#3f9d5c]" : "bg-kid-sun/20 text-[#B07A11]"}`} data-testid="coupons-mode">{mode === "live" ? "Live mode" : "Test mode"}</span>
          <Button onClick={sync} disabled={syncing} variant="outline" data-testid="coupons-sync">
            <RefreshCw size={16} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync from Stripe"}
          </Button>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="kid-card p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="coupon-create-form">
        <div className="sm:col-span-2 flex items-center gap-2 text-kid-navy font-bold"><Plus size={16} className="text-kid-teal" /> New coupon</div>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input value={form.name} onChange={set("name")} required placeholder="Welcome offer" data-testid="coupon-name" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>Promo code (optional)</Label>
          <Input value={form.code} onChange={set("code")} placeholder="WELCOME20" data-testid="coupon-code" className="h-11 rounded-xl uppercase" />
        </div>
        <div className="space-y-1.5">
          <Label>Discount type</Label>
          <select value={form.discount_type} onChange={set("discount_type")} data-testid="coupon-type" className="h-11 w-full rounded-xl border border-kid-line px-3 bg-white text-sm">
            <option value="percent">Percentage (%)</option>
            <option value="amount">Fixed amount (CAD)</option>
          </select>
        </div>
        <div className="space-y-1.5">
          {form.discount_type === "percent" ? (
            <>
              <Label>Percent off</Label>
              <Input type="number" min="1" max="100" value={form.percent_off} onChange={set("percent_off")} required placeholder="20" data-testid="coupon-percent" className="h-11 rounded-xl" />
            </>
          ) : (
            <>
              <Label>Amount off (CAD)</Label>
              <Input type="number" min="1" step="0.01" value={form.amount_off} onChange={set("amount_off")} required placeholder="5.00" data-testid="coupon-amount" className="h-11 rounded-xl" />
            </>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Duration</Label>
          <select value={form.duration} onChange={set("duration")} data-testid="coupon-duration" className="h-11 w-full rounded-xl border border-kid-line px-3 bg-white text-sm">
            <option value="once">Once</option>
            <option value="repeating">Repeating (months)</option>
            <option value="forever">Forever</option>
          </select>
        </div>
        {form.duration === "repeating" && (
          <div className="space-y-1.5">
            <Label>Months</Label>
            <Input type="number" min="1" value={form.duration_in_months} onChange={set("duration_in_months")} required placeholder="3" data-testid="coupon-months" className="h-11 rounded-xl" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Max redemptions (optional)</Label>
          <Input type="number" min="1" value={form.max_redemptions} onChange={set("max_redemptions")} placeholder="Unlimited" data-testid="coupon-max" className="h-11 rounded-xl" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={creating} data-testid="coupon-submit">{creating ? "Creating…" : "Create coupon"}</Button>
        </div>
      </form>

      {/* List */}
      {coupons.length === 0 ? (
        <div className="kid-card p-10 text-center text-kid-muted" data-testid="coupons-empty">
          <Ticket size={28} className="mx-auto mb-3 opacity-60" />
          No coupons yet. Create one above — it'll appear in your Stripe dashboard too.
        </div>
      ) : (
        <div className="kid-card overflow-hidden" data-testid="coupons-table">
          <table className="w-full text-sm">
            <thead className="bg-kid-mist text-kid-muted text-left"><tr>
              <th className="px-5 py-3 font-poppins font-semibold">Name</th>
              <th className="px-5 py-3">Code</th>
              <th className="px-5 py-3">Discount</th>
              <th className="px-5 py-3">Duration</th>
              <th className="px-5 py-3">Redeemed</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-t border-kid-line" data-testid="coupon-row">
                  <td className="px-5 py-3 font-medium text-kid-navy">{c.name}</td>
                  <td className="px-5 py-3">
                    {c.promo_code
                      ? <span className="inline-flex items-center gap-1 font-mono text-xs font-semibold px-2 py-1 rounded-lg bg-kid-teal/10 text-kid-teal"><Tag size={12} /> {c.promo_code}</span>
                      : <span className="text-kid-muted text-xs">No code</span>}
                  </td>
                  <td className="px-5 py-3 text-kid-navy font-semibold">{discountLabel(c)}</td>
                  <td className="px-5 py-3 text-kid-muted capitalize">{c.duration}{c.duration === "repeating" && c.duration_in_months ? ` · ${c.duration_in_months}mo` : ""}</td>
                  <td className="px-5 py-3 text-kid-muted">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setDeleting(c)} className="text-kid-coral p-1.5 rounded-lg hover:bg-kid-mist" title="Delete" data-testid="coupon-delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="coupon-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this coupon?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}"{deleting?.promo_code ? ` (${deleting.promo_code})` : ""} will be deleted from Stripe and can no longer be redeemed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="coupon-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="coupon-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
